import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Camera, Loader2, ScanLine, X } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toastHelpers } from '@/lib/toast-helpers'
import {
  cameraScanSupported,
  getScannerCamera,
  isTouchPrimary,
  listVideoInputs,
  setScannerAutoStart,
} from '@/lib/scannerCamera'
import {
  CameraCardScanner,
  ScanError,
  describeScanError,
} from './loyaltyCardScanner'
import type { Order } from '@/types'

interface CounterScanLoyaltyCardProps {
  /**
   * The open order to attach to, when one exists yet. On a brand-new cart the
   * order hasn't been created, so this is null and we fall back to onResolved.
   */
  orderId?: string | null
  /** Called with the updated order after a scanned card is attached. */
  onAttached?: (order: Order) => void
  /**
   * Called instead of onAttached when there is no order yet. The caller stashes
   * the customer in the local guest fields, which the existing counter flow
   * already carries into order creation — so the customer lands on the order
   * the moment it exists.
   */
  onResolved?: (guest: { name: string; phone: string }) => void
  /**
   * Fired while the live viewfinder is up. The counter's guest popover uses it
   * to stop closing itself on focus-outside — Chrome's camera permission bubble
   * steals focus, which would otherwise tear the popover (and the stream) down
   * mid-prompt.
   */
  onCameraActiveChange?: (active: boolean) => void
}

/**
 * Counter affordance for scanning a customer's wallet stamp card.
 *
 * Two ways in, both landing on the same code:
 *
 *   • A USB 2D barcode scanner in HID (keyboard) mode types the decoded
 *     card_token into the focused field and sends Enter — so a plain focused
 *     input with an Enter handler captures a scan reliably, and also lets a
 *     cashier type a code by hand.
 *   • The device camera, for counter staff working off a phone or tablet with
 *     no scanner hardware. The customer holds up their Apple/Google Wallet
 *     card and staff point the camera at it.
 *
 * Both are the same string: the wallet pass barcode encodes the raw
 * card_token, so a camera decode is indistinguishable from a scanner's
 * keystrokes. On submit we resolve the token to a customer (POST
 * /counter/loyalty/resolve-card) and attach them to the open order through
 * the SAME guest endpoint the search flow uses, so the loyalty chip lights up
 * exactly as if the cashier had picked them.
 */
export function CounterScanLoyaltyCard({
  orderId,
  onAttached,
  onResolved,
  onCameraActiveChange,
}: CounterScanLoyaltyCardProps) {
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [camera, setCamera] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) return
    // On a touch device don't steal focus while the viewfinder is up — the
    // on-screen keyboard would cover it. On a till (mouse/keyboard) we DO keep
    // the field focused with the camera live, because a USB scanner in HID mode
    // is a keyboard: both input paths then work at the same time.
    if (camera && isTouchPrimary()) return
    inputRef.current?.focus()
  }, [open, camera])

  // Open the viewfinder as soon as the panel opens, when this machine has a
  // camera and staff haven't turned auto-start off on it. Scanning a card has
  // to be a one-second reflex or nobody uses the loyalty programme.
  useEffect(() => {
    if (!open || !cameraScanSupported() || !getScannerCamera().autoStart) return
    let cancelled = false
    void listVideoInputs().then((devices) => {
      // No video input => this till is scanner-only; leave it alone.
      if (!cancelled && devices.length > 0) setCamera(true)
    })
    return () => {
      cancelled = true
    }
    // Deliberately keyed on `open` only: re-running when `camera` flips would
    // immediately reopen a viewfinder the cashier just closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    onCameraActiveChange?.(open && camera)
  }, [open, camera, onCameraActiveChange])

  // Releasing the camera on unmount is the scanner's own job, but the parent
  // must not be left believing it is still live.
  useEffect(() => () => onCameraActiveChange?.(false), [onCameraActiveChange])

  const scanMutation = useMutation({
    mutationFn: async (rawToken: string) => {
      const token = rawToken.trim()
      if (!token) throw new Error('empty')
      const res = await apiClient.resolveLoyaltyCard(token)
      const card = res.data
      if (!card || !card.enabled) {
        throw new ScanError('loyalty_off', 'Loyalty is off — enable it in admin settings.')
      }
      if (!card.customer_phone) {
        throw new ScanError('no_phone', 'This card has no phone on file to attach.')
      }
      const common = { name: card.customer_name, punches: card.punches, threshold: card.threshold }

      // No order yet (fresh cart) — resolving the card is enough. Hand the
      // guest details back to the counter, which already carries them into
      // order creation. Only the ATTACH needs an order id; the lookup doesn't.
      if (!orderId) {
        return { ...common, order: null, phone: card.customer_phone }
      }

      const attach = await apiClient.updateCounterOrderGuest(orderId, {
        customer_phone: card.customer_phone,
        customer_name: card.customer_name || undefined,
      })
      if (!attach.success || !attach.data) {
        throw new Error(attach.message || 'Could not attach the customer')
      }
      return { ...common, order: attach.data, phone: card.customer_phone }
    },
    onSuccess: ({ order, name, phone, punches, threshold }) => {
      setCode('')
      setCamera(false)
      setOpen(false)
      if (order) {
        onAttached?.(order)
      } else {
        onResolved?.({ name: name || '', phone })
      }
      toastHelpers.success(
        'Card scanned',
        `${name ? `${name} · ` : ''}${punches}/${threshold} visits ${order ? 'attached' : 'ready — finish the sale to add the stamp'}.`,
      )
    },
    onError: (err) => {
      const { title, description } = describeScanError(err)
      toastHelpers.error(title, description)
      // Keep the field open + clear it so the next scan can retry cleanly.
      setCode('')
      inputRef.current?.focus()
    },
  })

  function submit() {
    if (scanMutation.isPending) return
    if (!code.trim()) return
    scanMutation.mutate(code)
  }

  const onCameraDecode = useCallback(
    (token: string) => {
      setCamera(false)
      scanMutation.mutate(token)
    },
    [scanMutation],
  )

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full gap-1.5"
        onClick={() => setOpen(true)}
      >
        <ScanLine className="h-4 w-4" />
        Scan loyalty card
      </Button>
    )
  }

  return (
    <div className="space-y-1.5 rounded-md border border-border bg-background/60 p-2">
      {camera && (
        <CameraCardScanner
          onDecode={onCameraDecode}
          onClose={() => {
            // Closing by hand is a statement about THIS machine: don't pop the
            // webcam open on every guest lookup here. The button below brings
            // it back.
            setScannerAutoStart(false)
            setCamera(false)
          }}
          busy={scanMutation.isPending}
        />
      )}
      <div className="flex items-center gap-1.5">
        <Input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
          className="h-9 font-mono text-sm"
          placeholder="Scan card or enter code"
          autoComplete="off"
          spellCheck={false}
          disabled={scanMutation.isPending}
        />
        <Button
          type="button"
          size="sm"
          className="h-9 shrink-0"
          onClick={submit}
          disabled={scanMutation.isPending || !code.trim()}
        >
          {scanMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Attach'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-9 w-9 shrink-0 px-0"
          onClick={() => {
            setOpen(false)
            setCamera(false)
            setCode('')
          }}
          aria-label="Cancel scan"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      {!camera && cameraScanSupported() && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-full gap-1.5 text-xs"
          onClick={() => {
            setScannerAutoStart(true)
            setCamera(true)
          }}
          disabled={scanMutation.isPending}
        >
          <Camera className="h-3.5 w-3.5" />
          Scan with this device's camera
        </Button>
      )}
      <p className="text-[11px] leading-snug text-muted-foreground">
        Point the scanner at the customer's wallet card, or type the code.
      </p>
    </div>
  )
}

// CameraCardScanner, createDecoder and the error copy live in
// ./loyaltyCardScanner — shared with the staff-phone /scan surface so both
// drive the exact same camera stack.
